declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      on?: (event: string, handler: (response: Record<string, unknown>) => void) => void;
      open: () => void;
    };
  }
}

// In-flight load promise so concurrent callers share one download.
// Without this, a paywall preload + a subsequent rzp.open() click would
// inject two <script> tags, doubling network cost.
let _razorpayLoadPromise: Promise<void> | null = null;

export function loadRazorpayScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (_razorpayLoadPromise) return _razorpayLoadPromise;
  _razorpayLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _razorpayLoadPromise = null; // allow retry
      reject(new Error("Failed to load Razorpay"));
    };
    document.head.appendChild(script);
  });
  return _razorpayLoadPromise;
}

/**
 * Fire-and-forget warmup. Call when paywall surfaces (paywall_shown /
 * premium_unlock_shown) so the SDK is already cached by the time the user
 * clicks Pay. Cuts the report_checkout_started -> razorpay_checkout_opened
 * drop (90%+ on mobile per v_ga4_device_funnel_daily, 2026-05-24) caused
 * by slow-3G users abandoning during the lazy SDK download.
 */
export function preloadRazorpayScript(): void {
  if (typeof window === "undefined") return;
  // Idempotent — loadRazorpayScript returns the cached promise on repeat calls.
  loadRazorpayScript().catch(() => {
    // Best-effort. If the network is bad enough to fail here, the user
    // will see the proper error toast when they actually click Pay.
  });
}

// void@razorpay.com is Razorpay's anonymous-user default. Treat it as no
// email so the contact-capture gate fires and we don't lose the user to
// unreachable order history (trailing-30d: 53/151 attempts = 56.3% void).
const RAZORPAY_VOID_EMAIL = "void@razorpay.com";
// Minimal shape check (local@domain.tld, no whitespace). Malformed input
// degrades to "" so an optional-email typo never sends recovery_email_consent
// with an address the backend rejects; a phone buyer still checks out.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeContactEmail(value: string | undefined): string {
  const trimmed = (value ?? "").trim().toLowerCase();
  if (!EMAIL_SHAPE.test(trimmed) || trimmed === RAZORPAY_VOID_EMAIL) return "";
  return trimmed;
}

export function normalizeContactPhone(value: string | undefined): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed || trimmed.includes("@")) return "";
  return trimmed;
}
